import { useState, useCallback, useEffect, useMemo } from 'react'
import type { ArtefactTemplate, ArtefactTemplateScope } from '../../types/artefact-templates'
import type { ArtefactConfig } from '../../types/velociraptor'
import {
  getSaveAsCopyName,
  getTemplateSaveAction,
  type TemplateSaveAction,
  type TemplateUserRole,
} from '../../utils/template-permissions'
import { DEFAULT_COLLECTOR_SETTINGS, type CollectorSettings } from './CollectorSettingsForm'
import { useVelociraptorConfigSave } from './use-velociraptor-config-save'
import { useArtefactTemplateMutations } from '../../hooks/use-artefact-templates'

export type ActiveSection = 'metadata' | 'artefacts' | 'settings'

export type ParameterOverrides = Record<string, Record<string, string | number | boolean>>

interface UseConfigFormStateArgs {
  isOpen: boolean
  editTemplate: ArtefactTemplate | null | undefined
  saveAction: TemplateSaveAction
}

export interface ConfigFormState {
  name: string
  description: string
  platforms: string[]
  selectedArtefacts: string[]
  parameterOverrides: ParameterOverrides
  collectorSettings: CollectorSettings
  activeSection: ActiveSection
  saveError: string | null
  setName: (value: string) => void
  setDescription: (value: string) => void
  setCollectorSettings: (settings: CollectorSettings) => void
  setActiveSection: (section: ActiveSection) => void
  setSaveError: (error: string | null) => void
  togglePlatform: (platform: string) => void
  handleSelectArtefact: (artefactName: string) => void
  handleDeselectArtefact: (artefactName: string) => void
  handleParameterChange: (artefactName: string, paramName: string, value: string) => void
}

function buildOverridesFromTemplate(template: ArtefactTemplate): ParameterOverrides {
  const overrides: ParameterOverrides = {}
  for (const art of template.artefacts) {
    if (Object.keys(art.parameters).length > 0) {
      overrides[art.name] = art.parameters
    }
  }
  return overrides
}

interface FormSetters {
  setName: (v: string) => void
  setDescription: (v: string) => void
  setPlatforms: (v: string[]) => void
  setSelectedArtefacts: (v: string[]) => void
  setParameterOverrides: (v: ParameterOverrides) => void
  setCollectorSettings: (v: CollectorSettings) => void
}

function applyTemplateToForm(
  template: ArtefactTemplate,
  saveAction: TemplateSaveAction,
  setters: FormSetters
): void {
  const editName = saveAction === 'save_as' ? getSaveAsCopyName(template.name) : template.name
  setters.setName(editName)
  setters.setDescription(template.description ?? '')
  setters.setPlatforms(
    template.supportedPlatforms.length > 0 ? template.supportedPlatforms : ['windows']
  )
  setters.setSelectedArtefacts(template.artefacts.map((art) => art.name))
  setters.setParameterOverrides(buildOverridesFromTemplate(template))
}

function resetFormToDefaults(setters: FormSetters): void {
  setters.setName('')
  setters.setDescription('')
  setters.setPlatforms(['windows'])
  setters.setSelectedArtefacts([])
  setters.setParameterOverrides({})
  setters.setCollectorSettings(DEFAULT_COLLECTOR_SETTINGS)
}

export function useVelociraptorConfigFormState({
  isOpen,
  editTemplate,
  saveAction,
}: UseConfigFormStateArgs): ConfigFormState {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [platforms, setPlatforms] = useState<string[]>(['windows'])
  const [selectedArtefacts, setSelectedArtefacts] = useState<string[]>([])
  const [parameterOverrides, setParameterOverrides] = useState<ParameterOverrides>({})
  const [collectorSettings, setCollectorSettings] = useState<CollectorSettings>(
    DEFAULT_COLLECTOR_SETTINGS
  )
  const [activeSection, setActiveSection] = useState<ActiveSection>('metadata')
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    const setters: FormSetters = {
      setName,
      setDescription,
      setPlatforms,
      setSelectedArtefacts,
      setParameterOverrides,
      setCollectorSettings,
    }
    if (editTemplate) applyTemplateToForm(editTemplate, saveAction, setters)
    else resetFormToDefaults(setters)
    setActiveSection('metadata')
    setSaveError(null)
  }, [isOpen, editTemplate, saveAction])

  const handleSelectArtefact = useCallback((artefactName: string) => {
    setSelectedArtefacts((prev) => (prev.includes(artefactName) ? prev : [...prev, artefactName]))
  }, [])

  const handleDeselectArtefact = useCallback((artefactName: string) => {
    setSelectedArtefacts((prev) => prev.filter((existing) => existing !== artefactName))
  }, [])

  const handleParameterChange = useCallback(
    (artefactName: string, paramName: string, value: string) => {
      setParameterOverrides((prev) => ({
        ...prev,
        [artefactName]: { ...prev[artefactName], [paramName]: value },
      }))
    },
    []
  )

  const togglePlatform = useCallback((platform: string) => {
    setPlatforms((prev) =>
      prev.includes(platform)
        ? prev.filter((existing) => existing !== platform)
        : [...prev, platform]
    )
  }, [])

  return {
    name,
    description,
    platforms,
    selectedArtefacts,
    parameterOverrides,
    collectorSettings,
    activeSection,
    saveError,
    setName,
    setDescription,
    setCollectorSettings,
    setActiveSection,
    setSaveError,
    togglePlatform,
    handleSelectArtefact,
    handleDeselectArtefact,
    handleParameterChange,
  }
}

interface PlatformMutations {
  create: (input: {
    name: string
    description?: string
    artefacts: ArtefactConfig[]
    supportedPlatforms?: string[]
  }) => Promise<ArtefactTemplate | null>
  update: (
    templateId: string,
    input: {
      name?: string
      description?: string
      artefacts?: ArtefactConfig[]
      supportedPlatforms?: string[]
    }
  ) => Promise<ArtefactTemplate | null>
  creating: boolean
  updating: boolean
  error: string | null
}

interface OrchestrationArgs {
  isOpen: boolean
  editTemplate: ArtefactTemplate | null | undefined
  context: { scope: ArtefactTemplateScope; caseId?: string | undefined; userRole: TemplateUserRole }
  platformMutations: PlatformMutations | undefined
  onTemplateSaved: (template: ArtefactTemplate) => void
  onClose: () => void
}

export interface ConfigOrchestration {
  form: ConfigFormState
  saveAction: TemplateSaveAction
  isEditMode: boolean
  saving: boolean
  canSave: boolean
  mutationsError: string | null
  handleSave: () => void
}

function computeSaving(
  templateMutations: { creating: boolean; updating: boolean },
  platformMutations: PlatformMutations | undefined
): boolean {
  if (templateMutations.creating || templateMutations.updating) return true
  return Boolean(platformMutations?.creating) || Boolean(platformMutations?.updating)
}

export function useVelociraptorConfigOrchestration({
  isOpen,
  editTemplate,
  context,
  platformMutations,
  onTemplateSaved,
  onClose,
}: OrchestrationArgs): ConfigOrchestration {
  const mutations = useArtefactTemplateMutations()
  const saveAction: TemplateSaveAction = useMemo(() => {
    if (!editTemplate) return 'save'
    return getTemplateSaveAction({
      template: editTemplate,
      currentCaseId: context.caseId ?? null,
      userRole: context.userRole,
    })
  }, [editTemplate, context.caseId, context.userRole])

  const isEditMode = Boolean(editTemplate)
  const form = useVelociraptorConfigFormState({ isOpen, editTemplate, saveAction })
  const canSave = form.name.trim().length > 0 && form.selectedArtefacts.length > 0

  const executeSave = useVelociraptorConfigSave({
    saveAction,
    editTemplate,
    context,
    mutations,
    platformMutations,
    onTemplateSaved,
    onClose,
    setSaveError: form.setSaveError,
  })

  const handleSave = useCallback((): void => {
    if (!canSave) return
    void executeSave({
      name: form.name,
      description: form.description,
      selectedArtefacts: form.selectedArtefacts,
      parameterOverrides: form.parameterOverrides,
      platforms: form.platforms,
    })
  }, [
    canSave,
    executeSave,
    form.name,
    form.description,
    form.selectedArtefacts,
    form.parameterOverrides,
    form.platforms,
  ])

  return {
    form,
    saveAction,
    isEditMode,
    saving: computeSaving(mutations, platformMutations),
    canSave,
    mutationsError: mutations.error,
    handleSave,
  }
}
